import React, { useEffect, useState } from "react";
import { GetYearEndBoxValueHeaderById, GetYearEndBoxValuesById } from "../../../services/ImportDetailService";
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalFooterActions, Button } from "@ceridianhcm/components";
import BoxValueTable, { ParentBoxValueSummaryTable, ParentBoxValueSummaryRow } from "./YearEndBoxValueColumnsDefinition";
import logger from '../../../logger';
import { ContainerTypeDisplayName } from "../../../models/StandAloneData";

interface YearEndBoxValueModelProps {
  show: boolean;
  onClose: () => void;
  currentFile: CompanyImportData | BaseCompanyImport | null;
  containerTypeLookup: Record<number, string>;
}

interface BaseCompanyImport {
  companyName: string;
  companyId: string;
  importId: number;
  importDetailsId: number;
  userId: number;
  companyStatus: boolean;
  fileName: string;
  fileType: number;
  containerType: number;
  submissionType: number;
  isProduction: boolean;
  status: number;
  createdOn: string; 
}

export interface CompanyImportData extends BaseCompanyImport {
  _isParent?: boolean;
  _children?: BaseCompanyImport[];
}

interface YearEndBoxValueHeader {
  YearEndImportDetailId: number;
  ImportDetailsId: number;
  T4SlipCount: number;
  ContainerType: number | string | undefined;
  TaxYear: number;
  RunDate: string;
  CreatedBy: string;
  Status: number;
  CompanyName: string;
  FileName: string;
}

interface YearEndBoxValueHeaderResponse {
  yearEndImportDetailId: number;
  importDetailsId: number;
  t4SlipCount: number;
  taxYear: number;
  runDate: string;
  createdBy: string;
  boxCalculationStatus: number | null;
  status: number | null;
  companyName: string;
  fileName: string | null;
}

interface FileDataCache {
  header: YearEndBoxValueHeader;
  values: any[];
}

const YearEndBoxValueModel: React.FC<YearEndBoxValueModelProps> = ({ show, onClose , currentFile, containerTypeLookup }) => {
  const isParentMode = Boolean((currentFile as CompanyImportData | null)?._isParent);

  const [filesCache, setFilesCache] = useState<Map<number, FileDataCache>>(new Map());
  const [parentRows, setParentRows] = useState<ParentBoxValueSummaryRow[]>([]);
  const [selectedFile, setSelectedFile] = useState<ParentBoxValueSummaryRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [parentCompanyName, setParentCompanyName] = useState<string>("");
  
  const fetchSingleFileData = async (file: BaseCompanyImport): Promise<FileDataCache | null> => {
    try {
      const rawResponse = await GetYearEndBoxValueHeaderById(file.importDetailsId);
      
      let response: YearEndBoxValueHeaderResponse[];
      if (Array.isArray(rawResponse)) {
        response = rawResponse;
      } else if (rawResponse) {
        response = [rawResponse as unknown as YearEndBoxValueHeaderResponse];
      } else {
        response = [];
      }

      if (!response.length) {
        logger.warn("No response for importDetailsId:", file.importDetailsId);
        return null;
      }

      const headerResponse = response[0];
      const yearEndImportDetailId = headerResponse?.yearEndImportDetailId;

      if (!yearEndImportDetailId) {
        logger.warn("YearEndImportDetailId is undefined in response");
        return null;
      }

      const valuesResponse = await GetYearEndBoxValuesById(yearEndImportDetailId);
      const boxValues = Array.isArray(valuesResponse) ? valuesResponse : [];

      const header: YearEndBoxValueHeader = {
        YearEndImportDetailId: headerResponse.yearEndImportDetailId,
        ImportDetailsId: headerResponse.importDetailsId,
        T4SlipCount: headerResponse.t4SlipCount,
        ContainerType: file.containerType,
        TaxYear: headerResponse.taxYear,
        RunDate: formatRunDateToUTC(headerResponse.runDate),
        CreatedBy: headerResponse.createdBy,
        Status: headerResponse.boxCalculationStatus ?? headerResponse.status ?? 0,
        CompanyName: headerResponse.companyName,
        FileName: file.fileName || headerResponse.fileName || "",
      };

      return { header, values: boxValues };
    } catch (err) {
      logger.error("Error fetching file data:", err);
      return null;
    }
  };

  const createMinimalFileData = (file: BaseCompanyImport, companyName: string): FileDataCache => ({
    header: {
      YearEndImportDetailId: 0,
      ImportDetailsId: file.importDetailsId,
      T4SlipCount: 0,
      ContainerType: file.containerType,
      TaxYear: 0,
      RunDate: "",
      CreatedBy: "",
      Status: 0,
      CompanyName: companyName,
      FileName: file.fileName,
    },
    values: [],
  });

  const fetchParentModeData = async (parentFile: CompanyImportData) => {
    const children = parentFile._children ?? [];
    if (!children.length) {
      setLoading(false);
      return;
    }

    const cache = new Map<number, FileDataCache>();
    const summaryRows: ParentBoxValueSummaryRow[] = [];
    let companyName = "";
    let resolvedParentCompanyName = (currentFile as CompanyImportData)?.companyName || "";

    for (const child of children) {
      const fileData = await fetchSingleFileData(child);
      companyName = fileData?.header.CompanyName || companyName;
      if (!resolvedParentCompanyName && companyName) {
        resolvedParentCompanyName = companyName;
      }
      const resolvedContainerType = containerTypeLookup[child.containerType] || ContainerTypeDisplayName[child.containerType as keyof typeof ContainerTypeDisplayName] || String(child.containerType);
      
      const dataToCache = fileData || createMinimalFileData(child, companyName);
      cache.set(child.importDetailsId, dataToCache);

      const totals = fileData ? getParentWageTaxTotals(fileData.values, resolvedContainerType) : null;
      summaryRows.push({
        containerType: resolvedContainerType,
        YearEndImportDetailId: String(child.importDetailsId),
        totalFormCount: fileData?.header.T4SlipCount ?? 0,
        totalWageAmount: totals?.totalWage ?? 0,
        totalTaxAmount: totals?.totalTax ?? 0,
      });
    }

    setParentCompanyName(resolvedParentCompanyName);
    setFilesCache(cache);
    setParentRows(summaryRows);
  };

  const fetchSingleModeData = async (file: BaseCompanyImport) => {
    const fileData = await fetchSingleFileData(file);
    const cache = new Map<number, FileDataCache>();
    const dataToCache = fileData || createMinimalFileData(file, file.companyName);
    cache.set(file.importDetailsId, dataToCache);
    setFilesCache(cache);
  };

  const fetchAllData = async () => {
    if (!currentFile) {
      setLoading(false);
      setError(false);
      return;
    }

    setLoading(true);
    setError(false);

    try {
      if (isParentMode) {
        await fetchParentModeData(currentFile as CompanyImportData);
      } else if (currentFile.importDetailsId) {
        await fetchSingleModeData(currentFile as BaseCompanyImport);
      } else {
        setLoading(false);
      }
    } catch (err) {
      logger.error("Error fetching all data:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    
    setSelectedFile(null);
    fetchAllData();
  }, [show]);

  // Convert and format the run date to UTC (mm/dd/yyyy HH:mm:ss)
  const formatRunDateToUTC = (dateString: string) => {
    if (!dateString) {
      return "";
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const getParentWageTaxTotals = (boxValues: {boxName: string, boxValue: string}[], containerType: string) => {
    const containerTypeMap: Record<string, { wageBox: string; taxBox: string }> = {
      "T4": { wageBox: "14", taxBox: "22" },
      "T4A": { wageBox: "28", taxBox: "22" },
      "RL1": { wageBox: "A", taxBox: "E" },
      "RL2": { wageBox: "A", taxBox: "J" },
    };

    const config = containerTypeMap[containerType] || { wageBox: "", taxBox: "" };

    if (containerType === "NR4") {
      const totals = boxValues.reduce((runningTotals, box) => {
        const { boxName, boxValue } = box;
        const numericValue = parseFloat(boxValue) || 0;

        if (boxName === "16" || boxName === "26") {
          runningTotals.totalWage += numericValue;
        }

        if (boxName === "17" || boxName === "27") {
          runningTotals.totalTax += numericValue;
        }

        return runningTotals;
      }, { totalWage: 0, totalTax: 0 });

      return {
        totalWage: Math.round(totals.totalWage * 100) / 100,
        totalTax: Math.round(totals.totalTax * 100) / 100,
      };
    }

    return boxValues.reduce((totals, box) => {
      const { boxName, boxValue } = box;
      if (boxName === config.wageBox) {
        totals.totalWage = parseFloat(boxValue) || 0;
      }
      if (boxName === config.taxBox) {
        totals.totalTax = parseFloat(boxValue) || 0;
      }
      return totals;
    }, { totalWage: 0, totalTax: 0 });
  };

  const renderFileDetails = (fileData: FileDataCache) => {
    const { header, values } = fileData;
    const status = header.Status;
    const recordCount = header.T4SlipCount;
    const hasBoxValue = values.length > 0;
    
    if (hasBoxValue) {
      return <BoxValueTable boxValues={values} boxHeader={[header]} containerTypeLookup={containerTypeLookup}/>;
    }

    const fileName = header.FileName || (currentFile as BaseCompanyImport)?.fileName || "";
    const companyName = header.CompanyName;
    const containerType = typeof header.ContainerType === 'number' 
      ? containerTypeLookup[header.ContainerType] 
      : header.ContainerType || "";
    const taxYear = header.TaxYear || new Date().getFullYear();
    const reportTitle = containerType ? `${containerType} Management Report ${taxYear}` : "";

    let message: string;
    if (status === 0 || (status === 2 && recordCount === 0)) {
      message = "Box Calculation not available.";
    } else if (status === 2) {
      message = "Box calculation error. Please reprocess the file.";
    } else {
      message = "No box values available.";
    }

    return (
      <>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "94%" }}>
            {companyName && <div><h3>{companyName}</h3></div>}
            {reportTitle && <div style={{margin: "auto"}}><h3>{reportTitle}</h3></div>}
          </div>
          {fileName && <div><p style={{ marginBottom: '10px' }}><strong>File Name:</strong> {fileName}</p></div>}
        </div>
        <p>{message}</p>
      </>
    );
  };

  const renderParentSummary = () => {
    if (!parentRows.length) {
      return <p>No box values available.</p>;
    }

    return (
      <ParentBoxValueSummaryTable
        companyName={parentCompanyName || (currentFile as CompanyImportData)?.companyName || ""}
        fileName={(currentFile as CompanyImportData)?.fileName}
        rows={parentRows}
        onFileClick={(row) => setSelectedFile(row)}
      />
    );
  };

  const renderModalBody = () => {
    if (loading) return <p>Loading data...</p>;
    if (error) return <p>Unable to load box values.</p>;
    if (isParentMode && !selectedFile) return renderParentSummary();

    let importDetailsId: number | undefined;
    if (selectedFile) {
      importDetailsId = Number(selectedFile.YearEndImportDetailId);
    } else {
      importDetailsId = (currentFile as BaseCompanyImport)?.importDetailsId;
    }

    const fileData = filesCache.get(importDetailsId);
    
    return fileData ? renderFileDetails(fileData) : <p>No box values available.</p>;
  };

  return (
    <Modal
      id="YearEndBoxValueModel"
      open={show}
      onClose={onClose}
      size="fullscreen"
      ariaLabelledBy=""
    >
      <ModalHeader
        id="new-action-modal-header"
        title=""
        onCloseButtonClick={onClose}
        closeButtonAriaLabel="Close modal"
      />    

      <ModalBody id="new-action-modal-body">
        {isParentMode && selectedFile && (
          <Button
            id="back-button"
            label="Back"
            variant="primary"
            onClick={() => {
              setSelectedFile(null);
            }}
          />
        )}
        {renderModalBody()}
      </ModalBody>
      <ModalFooter id="new-action-modal-footer">
        <ModalFooterActions
          id="action-buttons"
          primaryAction={{
            id: "close-button",
            label: "Close",
            onClick: onClose,
          }}
        />
      </ModalFooter>
    </Modal>
  );
};

export default YearEndBoxValueModel;
